/**
 * Measuring text in a browser.
 *
 * A 2D canvas context rather than a hidden DOM element, for one reason that matters: `measureText`
 * does not touch the document, so it cannot force a layout or a style recalculation. Measuring by
 * inserting a `<span>` and reading `offsetWidth` does both, once per node, which is exactly the
 * cost the measurement cache exists to avoid paying.
 *
 * One context for the whole app. It holds no state between calls beyond the font it was last
 * asked about, and creating one per measurement would allocate a canvas per node.
 */
import { useEffect, useState } from 'react';

import {
  createApproximateMeasurer,
  createMeasureCache,
  wrapText,
  type CachingTextMeasurer,
  type TextMeasurer,
  type TextStyle,
} from '@orbit-mind/map-engine';

function fontOf(style: TextStyle): string {
  // The CSS `font` shorthand, in the order the spec requires: style, weight, size, family.
  return `${style.italic ? 'italic ' : ''}${String(style.fontWeight)} ${String(style.fontSize)}px ${style.fontFamily}`;
}

function createCanvasMeasurer(): TextMeasurer | null {
  if (typeof document === 'undefined') return null;
  const context = document.createElement('canvas').getContext('2d');
  if (context === null) return null;

  return {
    measure(text, style, maxWidth) {
      context.font = fontOf(style);
      const widthOf = (line: string): number => context.measureText(line).width;
      const lines = wrapText(text, maxWidth, widthOf);

      return {
        width: Math.min(maxWidth, Math.max(...lines.map(widthOf), 0)),
        // 1.35 line height, matching the approximate measurer so a headless render and a browser
        // render agree about how tall a node is.
        height: lines.length * style.fontSize * 1.35,
        lines,
      };
    },
  };
}

let shared: CachingTextMeasurer | null = null;

/**
 * The measurer this app uses.
 *
 * Falls back to the approximate one where a canvas context is unavailable — jsdom without the
 * canvas package, or a locked-down webview. The map renders slightly wrong rather than not at
 * all, which is the right way round: a mind map with imperfect node widths is still a mind map.
 */
export function textMeasurer(): CachingTextMeasurer {
  shared ??= createMeasureCache(createCanvasMeasurer() ?? createApproximateMeasurer());
  return shared;
}

/**
 * The face the map is drawn in, before and after the web font arrives.
 *
 * Two stacks rather than one, and the switch is the point. Node boxes are sized by measuring
 * their text, so the family a node was measured in has to be the family it is *drawn* in — and
 * for the first few hundred milliseconds of a cold load, that is not Inter. Naming the fallback
 * explicitly while the font is still downloading keeps the two in step: the measurement cache is
 * keyed by family, so the fallback's widths and Inter's widths are separate entries and neither
 * is ever read for the other.
 *
 * The alternative — measuring against `font-family: sans-serif` and hoping — produces boxes that
 * fit their text before the font loads and clip it afterwards, on every node, once per session.
 */
const FALLBACK_STACK = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const INTER_STACK = `"Inter Variable", "Inter", ${FALLBACK_STACK}`;

/** The size the map is drawn at. Node styles override per node; this is the base. */
export const CANVAS_FONT_SIZE = 14;

export function canvasTextStyle(fontFamily: string): TextStyle {
  return { fontFamily, fontSize: CANVAS_FONT_SIZE, fontWeight: 400, italic: false };
}

/**
 * Which stack to measure and draw with right now.
 *
 * `document.fonts.ready` resolves once every font the page asked for has settled — loaded or
 * failed. `check` afterwards is what distinguishes those two: a font that failed to download must
 * leave the map on the fallback stack rather than claiming a face the browser does not have.
 */
export function useCanvasFontFamily(): string {
  const [family, setFamily] = useState(FALLBACK_STACK);

  useEffect(() => {
    if (typeof document === 'undefined' || !('fonts' in document)) return;
    let cancelled = false;

    void document.fonts.ready.then(() => {
      if (cancelled) return;
      if (document.fonts.check(`400 ${String(CANVAS_FONT_SIZE)}px "Inter Variable"`)) {
        setFamily(INTER_STACK);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return family;
}
